import React, { useEffect, useRef } from "react";
import ApexCharts from "apexcharts";
import { attachPdfMenu } from "./chartPdf";

const HorizontalBarChart = ({
  chartdata,
  height = "450px",
  barColor = "#008FFB",
  xAxisTitle = "Value",
  tooltipUnit = "",
  decimalPoints = 0,
  exportName = "chart",
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    if (!chartdata?.categories || !chartdata?.series) return;

    // ✅ STEP 1: Sort data by value (highest first)
    const combined = chartdata.categories.map((cat, idx) => ({
      category: cat,
      value: chartdata.series[idx]
    }));
    
    combined.sort((a, b) => b.value - a.value);
    
    const sortedCategories = combined.map(item => item.category);
    const sortedSeries = combined.map(item => item.value);

    // ✅ STEP 2: Calculate dynamic height
    const rowHeight = 34;
    const numberOfItems = sortedCategories.length;
    const requested = typeof height === "number" ? height : parseInt(height, 10);
    const fitHeight = Math.max(360, numberOfItems * rowHeight + 56);
    const viewport = Number.isFinite(requested) ? requested : fitHeight;
    const internalHeight = Math.max(viewport, fitHeight);

    const options = {
      series: [
        {
          name: "Orders",
          data: sortedSeries,
        },
      ],
      chart: {
        type: "bar",
        height: internalHeight,
        redrawOnParentResize: false,
        redrawOnWindowResize: false,
        toolbar: { show: true },
        animations: { enabled: false },
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "62%",
          dataLabels: {
            position: "top",
          },
        },
      },
      colors: [barColor],
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          if (val === 0) return "";
          return Number(val).toFixed(decimalPoints);
        },
        offsetX: 6,
        style: {
          fontSize: "12px",
          colors: ["#14233d"],
          fontWeight: 600,
        },
      },
      xaxis: {
        categories: sortedCategories,
        position: "top",
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            fontSize: "11px",
            colors: "#607D8B",
          },
        },
      },
      yaxis: {
        labels: {
          show: true,
          align: "right",
          minWidth: 132,
          maxWidth: 168,
          trim: true,
          style: {
            fontSize: "12px",
            fontWeight: 500,
            colors: "#334155",
          },
          formatter: (value) => {
            const text = String(value || "");
            return text.length > 24 ? `${text.slice(0, 22)}…` : text;
          },
        },
      },
      grid: {
        show: true,
        borderColor: "#e8eaf6",
        strokeDashArray: 4,
        padding: {
          top: 4,
          right: 28,
          bottom: 0,
          left: 12,
        },
        xaxis: {
          lines: {
            show: true
          }
        }
      },
      tooltip: {
        enabled: true,
        theme: "light",
        fillSeriesColor: false,
        x: {
          formatter: (val) => val,
        },
        y: {
          title: { formatter: () => "Orders:" },
          formatter: (val) => `${Number(val).toFixed(decimalPoints)} ${tooltipUnit}`.trim(),
        },
      },
    };

    if (!chartRef.current) return;
    chartRef.current.style.minHeight = `${internalHeight}px`;
    chartInstance.current = new ApexCharts(chartRef.current, options);
    const root = chartRef.current;
    chartInstance.current.render().then(() => attachPdfMenu(chartInstance.current, root, exportName));

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [chartdata, barColor, tooltipUnit, decimalPoints, exportName, height]);

  const viewport = typeof height === "number" ? height : parseInt(height, 10);

  return (
    <div className="dash-hbar">
      {!chartdata ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p style={{ color: "#757575", fontSize: "14px" }}>No data available</p>
        </div>
      ) : (
        <>
          {xAxisTitle ? <div className="dash-hbar-caption">{xAxisTitle}</div> : null}
          <div
            className="dash-hbar-scroll"
            style={{ maxHeight: Number.isFinite(viewport) ? viewport : undefined }}
          >
            <div ref={chartRef} style={{ width: "100%" }}></div>
          </div>
        </>
      )}
    </div>
  );
};

export default HorizontalBarChart;